import './index.css'

import clsx from 'clsx'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { isBefore, isValid } from 'date-fns'
import React from 'react'
import { Link } from 'react-router-dom'

import api from '@disco/api/server'
import { PROCESSING } from '@disco/constants/WatermarkStatusesTypes'
import type { WatermarkingQuota } from '@disco/utils/getWatermarkingQuota'
import {
  formatScheduledCampaignData,
  getTimeDateToScheduleEmail,
} from '@disco/utils/emailCampaigns'
import { guessCurrentTimezone } from '@disco/utils/time-utils'

import type { User } from 'types/user'
import type { ScheduledValue } from 'types/campaign'

import { Box, Flex, IconButton, TextField } from 'components/ds'
import TextFieldInput from 'components/ui/text-field'
import { EmailSchedulerModal } from 'components/modals/email-scheduler'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ContactUsLink from 'components/ui/contact-us-link'
import Icon from 'components/ui/icon'
import InlineLink from 'components/ui/inline-link'
import PricingBadge from 'components/ui/pricing-badge'
import PricingPopover from 'components/ui/pricing-popover'
import Stack from 'components/ui/stack'
import SwitchContainer from 'components/ui/switch/container'

dayjs.extend(utc)
dayjs.extend(timezone)

const EN_SPACE = ' '
const INTERCOM_MESSAGE =
  "I'm interested in upgrading to Pro to access playlist security features"

function noDblClick(evt) {
  if (evt.detail > 1) evt.preventDefault()
}

class SecurityTab extends React.Component<
  {
    user: User
    playlistBaseObj: Record<string, any>
    shareSecurityEnabled: boolean
    pushUINotification: (...args: Array<any>) => any
    sendMixpanelEvent: (...args: Array<any>) => any
    setPlaylistBaseObj: (...args: Array<any>) => any
    watermarkingEnabled: boolean
    watermarkingQuota: WatermarkingQuota
    playlistSettings?: boolean
    isTrial?: boolean
    shortlist?: Record<string, any>
  },
  {
    expandPassword: boolean
    expandExpiry: boolean
    expiry: Date | null
    password: string
    isSavingPassword: boolean
    isSavingExpiry: boolean
    isExpiryModalOpen: boolean
    watermark: boolean
    streamingOnly: boolean
    watermarkingLoading: boolean
  }
> {
  state: {
    expandExpiry: boolean
    expandPassword: boolean
    expiry: Date | null
    isExpiryModalOpen: boolean
    isSavingExpiry: boolean
    isSavingPassword: boolean
    password: string
    streamingOnly: boolean
    watermark: boolean
    watermarkingLoading: boolean
  } = {
    expandPassword: !!this.props.playlistBaseObj.password,
    expandExpiry: !!this.props.playlistBaseObj.expiry_datetime,
    expiry: this.props.playlistBaseObj.expiry_datetime,
    password: this.props.playlistBaseObj.password || '',
    watermark: this.props.playlistBaseObj.watermark,
    streamingOnly: !this.props.playlistBaseObj.download_links,
    isSavingPassword: false,
    isSavingExpiry: false,
    isExpiryModalOpen: false,
    watermarkingLoading: false,
  }

  renderAlert(): React.ReactNode {
    return (
      <Alert className="SecurityTab_Alert">
        Password protection and link expiry are available on
        <Button
          compact
          appearance="inline-link"
          className="SecurityTab_AlertLink"
          link="/pricing"
          caption="Pro"
        />
      </Alert>
    )
  }

  renderLockScreen(): React.ReactNode {
    return (
      <>
        <h3 className="SecurityTab_LockTitle">Playlist security 🔒</h3>
        <p className="SecurityTab_LockDescription">
          With playlist security features, you can set your share links to
          expire, or add a password to them for extra security.
        </p>
        <ContactUsLink intercomMsg={INTERCOM_MESSAGE}>
          <Button
            appearance="primary"
            className="SecurityTab_LockBtn"
            caption="Upgrade"
          />
        </ContactUsLink>
      </>
    )
  }

  renderPricingPopover(props: {
    children: React.ReactNode
    description: React.ReactNode
    messageClassName?: string
  }): React.ReactNode | React.ReactNode {
    if (props.description) {
      return <PricingPopover {...props} />
    }

    return props.children
  }

  onSetExpiryScheduleData = async ({
    date,
    time,
    timeZone,
  }: {
    date: string
    time: ScheduledValue
    timeZone: ScheduledValue
  }) => {
    const expiryIso = getTimeDateToScheduleEmail(
      date as any,
      time.value,
      timeZone.value,
    )
    const previousExpiry = this.state.expiry
      ? new Date(this.state.expiry)
      : null
    const newExpiry = new Date(expiryIso)
    this.props.setPlaylistBaseObj({ expiry_datetime: newExpiry })
    this.setState({
      expiry: newExpiry,
      expandExpiry: true,
      isSavingExpiry: true,
    })
    try {
      await api.queueCall(() =>
        api.updatePlaylistBaseObj(this.props.playlistBaseObj.id, {
          expiry_datetime: expiryIso,
        }),
      )
      this.props.pushUINotification({
        message: 'Link expiry set!',
        level: 'success',
      })
    } catch (err) {
      this.props.setPlaylistBaseObj({
        expiry_datetime: previousExpiry,
      })
      this.setState({ expiry: previousExpiry })
      this.props.pushUINotification({
        message: 'Sorry! Failed to set link expiry.',
        level: 'error',
      })
      console.error(err)
    } finally {
      this.setState({ isSavingExpiry: false })
    }
  }

  onSavePassword: (event: React.SyntheticEvent<EventTarget>) => Promise<void> =
    async (event: React.SyntheticEvent<any>) => {
      event.preventDefault()
      const { playlistBaseObj, shareSecurityEnabled } = this.props
      if (!this.state.password || !shareSecurityEnabled) return
      const playlistId = playlistBaseObj.id
      const playlistPassword = this.state.password
      this.props.setPlaylistBaseObj({
        password: playlistPassword,
      })
      this.setState({
        isSavingPassword: true,
      })

      try {
        await api.queueCall(() =>
          api.updatePlaylistBaseObj(playlistId, {
            password: playlistPassword,
          }),
        )
        this.props.pushUINotification({
          message: 'Password added!',
          level: 'success',
        })
      } catch (err) {
        this.props.pushUINotification({
          message: 'Sorry! Failed to add password.',
          level: 'error',
        })
        console.error(err)
      }

      this.setState({
        isSavingPassword: false,
      })
    }

  render(): React.ReactNode | React.ReactElement<'div'> {
    const {
      expandPassword,
      expandExpiry,
      isExpiryModalOpen,
      watermark,
      streamingOnly,
      watermarkingLoading,
    } = this.state
    const {
      shareSecurityEnabled,
      isTrial,
      watermarkingEnabled,
      playlistBaseObj,
      playlistSettings,
      watermarkingQuota,
      shortlist,
    } = this.props

    if (
      !isTrial &&
      !shareSecurityEnabled &&
      !this.state.password &&
      !this.state.expiry &&
      !playlistSettings
    ) {
      return this.renderLockScreen()
    }

    // TODO: replace shareSecurityEnabled with PASSWORD_PROTECTION and URL_EXPIRY_DATE
    // https://disco-team.atlassian.net/browse/DISCO-744
    const passwordProtectionEnabled = shareSecurityEnabled
    const linkExpiryEnabled = shareSecurityEnabled
    const expiry = this.state.expiry ? new Date(this.state.expiry) : null
    const isValidExpiry = expiry && isValid(expiry)
    const hasAlreadyExpired =
      expiry && isValidExpiry && isBefore(expiry, new Date())
    const formattedExpiry =
      expiry && isValidExpiry
        ? dayjs(expiry).format('D MMM YYYY, h:mm A')
        : null
    const localTimezone = guessCurrentTimezone()
    const storedCampaignSchedule =
      expiry && isValidExpiry && localTimezone?.value
        ? formatScheduledCampaignData({
            scheduled_at: expiry.toISOString(),
            scheduled_at_timezone: localTimezone.value,
            status: 'draft',
          })
        : undefined
    const passwordCheckboxDisabled =
      !passwordProtectionEnabled && !expandPassword
    const linkExpiryCheckboxDisabled = !linkExpiryEnabled && !expandExpiry
    const watermarkingProcessing =
      playlistBaseObj.watermarking_status === PROCESSING
    const watermarkingDisabled =
      !watermarkingEnabled ||
      watermarkingProcessing ||
      watermarkingQuota.overQuota
    let watermarkingTooltip = null
    const playlistId = playlistBaseObj.id

    if (watermarkingProcessing) {
      watermarkingTooltip = (
        <>
          This playlist is currently being watermarked, so this setting can’t be
          changed.
          <br /> You’ll be able to change this setting once the watermarking
          completes.
        </>
      )
    }

    if (watermarkingQuota.overQuota) {
      watermarkingTooltip = (
        <>
          You’ve reached your watermarking quota. <br /> To watermark more
          playlists, <InlineLink to="/settings/billing">upgrade</InlineLink> for
          <br /> more watermarking quota, or remove <br />
          watermarking from another playlist.
        </>
      )
    }

    if (!watermarkingEnabled) {
      watermarkingTooltip = (
        <>
          <div>Watermark your tracks for extra security.</div>
          <Link to="/watermarking" className="SecurityTab_Link">
            Find out more
          </Link>
        </>
      )
    }

    return (
      <div
        className={clsx({
          SecurityTab_Cont: !playlistSettings,
        })}
      >
        {(!shareSecurityEnabled || isTrial) &&
          (expandPassword || expandExpiry) &&
          this.renderAlert()}
        <Stack space="xsmall">
          {!playlistSettings && (
            <div
              className={clsx('SecurityTab_SecuritySection header', {
                'SecurityTab_SecuritySection--Loading': watermarkingLoading,
              })}
              onMouseDown={noDblClick}
              onClick={async evt => {
                evt.preventDefault()
                if (watermarkingDisabled || watermarkingLoading) return
                const currentWatermark = watermark
                const newWatermarkValue = !watermark
                this.setState({
                  watermark: newWatermarkValue,
                  watermarkingLoading: true,
                })
                this.props.sendMixpanelEvent(
                  currentWatermark
                    ? 'Security tab - Watermark off'
                    : 'Security tab - Watermark on',
                )
                this.props.setPlaylistBaseObj({
                  watermark: newWatermarkValue,
                })

                try {
                  await api.queueCall(() =>
                    api
                      .updatePlaylistBaseObj(playlistId, {
                        watermark: newWatermarkValue,
                      })
                      .then(updatedPlaylist => {
                        this.props.setPlaylistBaseObj({
                          watermarking_status:
                            updatedPlaylist.watermarking_status,
                        })
                      }),
                  )
                  this.setState({
                    watermarkingLoading: false,
                  })
                  this.props.pushUINotification({
                    message: `Watermark ${
                      currentWatermark ? 'removed' : 'set'
                    }!`,
                    level: 'success',
                  })
                } catch (err) {
                  this.props.pushUINotification({
                    message: `Sorry! Failed to ${
                      currentWatermark ? 'remove' : 'set'
                    } watermark.`,
                    level: 'error',
                  })
                  this.setState({
                    watermark: currentWatermark,
                    watermarkingLoading: false,
                  })
                  console.error(err)
                }
              }}
            >
              <SwitchContainer
                key="Watermark playlist"
                disabled={watermarkingDisabled}
              >
                {this.renderPricingPopover({
                  description: watermarkingTooltip,
                  children: (
                    <SwitchContainer.SwitchLabel label="Watermark playlist" />
                  ),
                })}
                <SwitchContainer.Switch
                  value={watermark}
                  disabled={watermarkingDisabled}
                />
              </SwitchContainer>
            </div>
          )}
          <div
            className="SecurityTab_SecuritySection header"
            onMouseDown={noDblClick}
            onClick={async evt => {
              evt.preventDefault()

              if (expandExpiry) {
                const currentExpiry = expiry
                this.setState({
                  expandExpiry: false,
                  expiry: null,
                })

                if (!isValidExpiry) {
                  return
                }

                this.props.sendMixpanelEvent('Security tab - Link expiry off')
                this.props.setPlaylistBaseObj({
                  expiry_datetime: null,
                })

                try {
                  await api.queueCall(() =>
                    api.updatePlaylistBaseObj(playlistId, {
                      expiry_datetime: null,
                    }),
                  )
                  this.props.pushUINotification({
                    message: 'Link expiry removed!',
                    level: 'success',
                  })
                } catch (err) {
                  this.props.pushUINotification({
                    message: 'Sorry! Failed to remove link expiry.',
                    level: 'error',
                  })
                  this.setState({
                    expandExpiry: true,
                    expiry: currentExpiry,
                  })
                  console.error(err)
                }
              } else {
                if (!linkExpiryCheckboxDisabled) {
                  this.props.sendMixpanelEvent('Security tab - Link expiry on')
                  this.setState({
                    expandExpiry: true,
                    isExpiryModalOpen: true,
                  })
                }
              }
            }}
          >
            <SwitchContainer key="Link expiry">
              <SwitchContainer.SwitchLabel
                label={
                  <>
                    Link expiry{EN_SPACE}
                    {linkExpiryEnabled ? null : (
                      <PricingBadge
                        title="Upgrade"
                        intercomMsg={INTERCOM_MESSAGE}
                      />
                    )}
                  </>
                }
              />
              <SwitchContainer.Switch
                value={expandExpiry}
                onChange={event =>
                  this.setState({
                    expandExpiry: event,
                  })
                }
                disabled={linkExpiryCheckboxDisabled}
              />
            </SwitchContainer>
          </div>
          {expandExpiry && (
            <div
              className="SecurityTab_SecuritySection form"
              onMouseDown={noDblClick}
              data-testid="SecurityTab_EditExpiry"
            >
              <div
                style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
              >
                <TextField.Root
                  size="2"
                  type="text"
                  readOnly
                  className="SecurityTab_ExpiryInput"
                  value={
                    storedCampaignSchedule
                      ? `${storedCampaignSchedule.date.label}, ${storedCampaignSchedule.time.label} ${storedCampaignSchedule.timeZone.value}`
                      : ''
                  }
                  placeholder="Set expiry date and time"
                  onClick={() => {
                    if (shareSecurityEnabled)
                      this.setState({ isExpiryModalOpen: true })
                  }}
                  style={{
                    cursor: shareSecurityEnabled ? 'pointer' : 'default',
                  }}
                >
                  {isValidExpiry && (
                    <TextField.Slot
                      side="right"
                      tabIndex={-1}
                      style={{ cursor: 'pointer' }}
                    >
                      <IconButton
                        type="button"
                        size="1"
                        tabIndex={-1}
                        variant="ghost"
                        color="gray"
                        onClick={async e => {
                          e.stopPropagation()
                          const currentExpiry = expiry
                          this.setState({ expiry: null })
                          this.props.setPlaylistBaseObj({
                            expiry_datetime: null,
                          })
                          try {
                            await api.queueCall(() =>
                              api.updatePlaylistBaseObj(playlistId, {
                                expiry_datetime: null,
                              }),
                            )
                            this.props.pushUINotification({
                              message: 'Link expiry removed!',
                              level: 'success',
                            })
                          } catch (err) {
                            this.setState({ expiry: currentExpiry })
                            this.props.pushUINotification({
                              message: 'Sorry! Failed to remove link expiry.',
                              level: 'error',
                            })
                            console.error(err)
                          }
                        }}
                      >
                        <Icon type="close" width={16} height={16} />
                      </IconButton>
                    </TextField.Slot>
                  )}
                  <TextField.Slot side="right" tabIndex={-1}>
                    <IconButton
                      type="button"
                      size="1"
                      tabIndex={-1}
                      variant="ghost"
                      color="gray"
                      onClick={() => {
                        if (shareSecurityEnabled)
                          this.setState({ isExpiryModalOpen: true })
                      }}
                    >
                      <Icon type="calendar" width={16} height={16} />
                    </IconButton>
                  </TextField.Slot>
                </TextField.Root>
                {isValidExpiry && localTimezone?.value && (
                  <p className="SecurityTab_ExpiryHint">
                    {`Displayed in your local timezone (${localTimezone.value})`}
                  </p>
                )}
              </div>
            </div>
          )}
          {isExpiryModalOpen && (
            <EmailSchedulerModal
              caption="Link expiry"
              description="Pick the date, time and timezone when this link should expire."
              primaryButtonCaption="Set expiry"
              preserveUtcOnTimezoneChange
              initialUtcDatetime={expiry ? expiry.toISOString() : undefined}
              showCloseButton
              storedCampaignSchedule={storedCampaignSchedule ?? undefined}
              onClose={() => this.setState({ isExpiryModalOpen: false })}
              setScheduleData={this.onSetExpiryScheduleData}
            />
          )}
          <div
            className="SecurityTab_SecuritySection header"
            onMouseDown={noDblClick}
            onClick={async evt => {
              evt.preventDefault()

              if (expandPassword) {
                const currentPassword = this.state.password
                this.setState({
                  expandPassword: false,
                  password: '',
                })

                if (!currentPassword) {
                  return
                }

                this.props.sendMixpanelEvent(
                  'Security tab - Password protect off',
                )
                this.props.setPlaylistBaseObj({
                  password: null,
                })

                try {
                  await api.queueCall(() =>
                    api.updatePlaylistBaseObj(playlistId, {
                      password: null,
                    }),
                  )
                  this.props.pushUINotification({
                    message: 'Password removed!',
                    level: 'success',
                  })
                } catch (err) {
                  this.props.pushUINotification({
                    message: 'Sorry! Failed to remove password.',
                    level: 'error',
                  })
                  this.setState({
                    expandPassword: true,
                    password: currentPassword,
                  })
                  console.error(err)
                }
              } else {
                if (!passwordCheckboxDisabled) {
                  this.props.sendMixpanelEvent(
                    'Security tab - Password protect on',
                  )
                  this.setState({
                    expandPassword: !expandPassword,
                  })
                }
              }
            }}
          >
            <SwitchContainer key="Password protect">
              <SwitchContainer.SwitchLabel
                label={
                  <>
                    Password protect{EN_SPACE}
                    {passwordProtectionEnabled ? null : (
                      <PricingBadge
                        title="Upgrade"
                        intercomMsg={INTERCOM_MESSAGE}
                      />
                    )}
                  </>
                }
              />
              <SwitchContainer.Switch
                value={expandPassword}
                onChange={event =>
                  this.setState({
                    expandPassword: event,
                  })
                }
                disabled={passwordCheckboxDisabled}
              />
            </SwitchContainer>
          </div>
          {expandPassword && (
            <Flex gap="2">
              <Box flexGrow="1">
                <TextFieldInput
                  autoComplete="off"
                  disabled={!shareSecurityEnabled}
                  placeholder="Input Password"
                  value={this.state.password}
                  onChange={evt =>
                    this.setState({
                      password: evt.target.value,
                    })
                  }
                />
              </Box>
              <Button
                disabled={!this.state.password || !shareSecurityEnabled}
                caption="Save password"
                progressCaption="Saving"
                progressIndicator={this.state.isSavingPassword}
                onClick={this.onSavePassword}
              />
            </Flex>
          )}
          <div
            className="SecurityTab_SecuritySection header"
            onMouseDown={noDblClick}
            onClick={async evt => {
              evt.preventDefault()
              const currentStreamingOnly = streamingOnly
              this.setState({
                streamingOnly: !streamingOnly,
              })
              const downloadLinks = !!streamingOnly
              this.props.sendMixpanelEvent(
                currentStreamingOnly
                  ? 'Security tab - Streaming only off'
                  : 'Security tab - Streaming only on',
              )
              this.props.setPlaylistBaseObj({
                download_links: downloadLinks,
              })

              try {
                await api.queueCall(() =>
                  api.updatePlaylistBaseObj(playlistId, {
                    download_links: downloadLinks,
                  }),
                )
                this.props.pushUINotification({
                  message: `Streaming only ${
                    currentStreamingOnly ? 'removed' : 'set'
                  }!`,
                  level: 'success',
                })
              } catch (err) {
                this.props.pushUINotification({
                  message: `Sorry! Failed to ${
                    currentStreamingOnly ? 'remove' : 'set'
                  } streaming only.`,
                  level: 'error',
                })
                this.setState({
                  streamingOnly: currentStreamingOnly,
                })
                console.error(err)
              }
            }}
          >
            <>
              {!playlistSettings && (
                <SwitchContainer key="Streaming only">
                  <SwitchContainer.SwitchLabel label="Streaming only" />
                  <SwitchContainer.Switch
                    value={streamingOnly}
                    onChange={checked =>
                      this.setState({
                        streamingOnly: checked,
                      })
                    }
                  />
                </SwitchContainer>
              )}
            </>
          </div>
        </Stack>
      </div>
    )
  }
}

export default SecurityTab
